import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FileService } from '../../../core/services/file.service';
import { CharityFileType } from '../../../core/models/file.model';

@Component({
  selector: 'afra-image',
  styleUrl: './afra-image.component.scss',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './afra-image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AfraImageComponent implements OnChanges {
  @Input() fileId: string | null | undefined;
  @Input() dataUrl: string | null | undefined;
  @Input() fileType: CharityFileType = CharityFileType.IMAGE;
  @Input() alt = '';
  /** CSS class(es) to apply to the img, like a regular img tag. */
  @Input() imgClass = '';

  @Input() imageTemplate: TemplateRef<any> | null = null;

  context = signal<{ src: string | null; alt: string; imgClass: string }>({
    src: null,
    alt: '',
    imgClass: '',
  });

  private readonly fileService = inject(FileService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    // We want to trigger loadImage if any relevant input changes, but only once per change detection cycle if multiple change.
    if (
      changes['fileId'] ||
      changes['dataUrl'] ||
      changes['fileType'] ||
      changes['alt'] ||
      changes['imgClass']
    ) {
      this.loadImage();
    }
  }

  private loadImage(): void {
    const alt = this.alt;
    const className = this.imgClass;

    if (this.dataUrl) {
      this.context.set({ src: this.dataUrl, alt, imgClass: className });
      this.cdr.markForCheck();
      return;
    }

    const id = this.fileId;
    if (!id) {
      this.context.set({ src: null, alt, imgClass: className });
      this.cdr.markForCheck();
      return;
    }

    this.fileService
      .getFileAsDataUrl(id, this.fileType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dataUrl) => {
        this.context.set({ src: dataUrl, alt, imgClass: className });
        this.cdr.markForCheck();
      });
  }
}
